import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import moment from 'moment';

import IndividualValidationResult from 'components/IndividualValidationResult';
import WhdStatementValidationResult from 'components/WhdStatementValidationResult';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import FormWorkflowStages from 'common/Workflows/FormWorkflowStages';
import FormTypes from 'common/Forms/FormTypes';

import { downloadFormSummary } from 'actions';

class ValidationResultsSection extends Component {
  static propTypes = {
    validationResult: PropTypes.shape(),
    whdResult: PropTypes.shape(),
    isValidatingForm: PropTypes.bool,
    isValid: PropTypes.bool,
    isOnlineForm: PropTypes.bool,
    showWitholdingValidation: PropTypes.bool,
    piiValidationCodes: PropTypes.shape(),
    isPiiProtectionEnabled: PropTypes.bool,
    tinMatchResult: PropTypes.arrayOf(PropTypes.shape()),
    onCheckTin: PropTypes.func,
    showTinMatching: PropTypes.bool,
    tinMatchFormProps: PropTypes.shape(),
    isFormDirty: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    isDownloadingFormSummary: PropTypes.bool,
    permissions: PropTypes.arrayOf(PropTypes.string),
  };

  static defaultProps = {
    validationResult: null,
    whdResult: null,
    isValidatingForm: false,
    isValid: false,
    isOnlineForm: false,
    showWitholdingValidation: false,
    piiValidationCodes: null,
    isPiiProtectionEnabled: false,
    tinMatchResult: null,
    onCheckTin: null,
    showTinMatching: false,
    tinMatchFormProps: null,
    isFormDirty: false,
    isDownloadingFormSummary: false,
    permissions: null,
  };

  onDownloadResultsClick = () => {
    const { dispatch, validationResult, isDownloadingFormSummary } = this.props;
    if (validationResult && !isDownloadingFormSummary) {
      dispatch(downloadFormSummary(validationResult.formId, validationResult.customerDbId, validationResult.accountId));
    }
  };

  getFormValidityStatus() {
    const { validationResult } = this.props;

    let statusText = '';
    if (validationResult) {
      statusText = this.isFormValid() ? 'Valid' : 'Invalid';
      statusText += this.isFormValid() && this.isCompletedForm() ? ' (Not Expired)' : '';
      statusText += this.isFormValid() && validationResult && validationResult.isWithTreaty ? ' with Treaty' : '';
      statusText += validationResult && validationResult.isLocked ? ' & Locked' : '';
    }

    return statusText;
  }

  getChildTinMatchFormProps(formId) {
    const { tinMatchResult } = this.props;
    const childResult = tinMatchResult.find(x => x.formId === formId);

    const tinTypeSsn = 1;
    const tinTypeEin = 2;

    if (childResult) {
      return {
        name: childResult.name,
        tin: {
          ssn: `${ childResult.tinType === tinTypeSsn ? childResult.tin : '' }`,
          ein: `${ childResult.tinType === tinTypeEin ? childResult.tin : '' }`
        }
      };
    }

    return null;
  }

  isValidated() {
    const { validationResult } = this.props;
    return (validationResult && validationResult.isValidated);
  }

  isFormValid() {
    const { isValid } = this.props;
    return isValid;
  }

  isCompletedForm() {
    const { validationResult } = this.props;
    return (validationResult && validationResult.workflowStage === FormWorkflowStages.Complete);
  }

  isExpired() {
    const { validationResult } = this.props;
    if (!validationResult.validUntil) return false;

    const formatedDate = moment(validationResult.validUntil).format('YYYY MM DD');
    const m = moment();
    m.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    return m.diff(formatedDate) > 0;
  }

  expiryDisplayText(formFamily) {
    const { validationResult } = this.props;
    return <div>
      <h2 className="invalid">
        { formFamily }: Expired on { moment(validationResult.validUntil).format('ll') }
        <p className="sub-text">Expiration due to { validationResult.expiryReasonDescription }</p>
      </h2>
    </div>;
  }

  render() {
    const {
      isValidatingForm,
      isOnlineForm,
      validationResult,
      whdResult,
      showWitholdingValidation,
      piiValidationCodes,
      isPiiProtectionEnabled,
      tinMatchResult,
      showTinMatching,
      onCheckTin,
      tinMatchFormProps,
      isFormDirty,
      isDownloadingFormSummary,
      permissions
    } = this.props;

    const formFamily = validationResult.formType && FormTypes[validationResult.formType.toLowerCase()] && FormTypes[validationResult.formType.toLowerCase()].getFamilyName();
    const formType = validationResult?.formType;
    const isImy = formType?.indexOf('IMY') > -1;
    const overallValidity = isImy ? this.isFormValid() && whdResult?.valid : this.isFormValid();
    const isSummaryDownloadEnabled = ['w8ben_2021', 'w9_2024', 'w8eci_2021'].includes(validationResult.formType.toLowerCase());

    return (
      <div className="validation-results">
        <h1 className="mb-0">Validation Results</h1>
        <div className="result-status">
          <h1>Account: {validationResult && validationResult.accountNumber}</h1>
          {this.isExpired() && this.isFormValid() && this.expiryDisplayText(formFamily)}
          {(!this.isExpired() || !this.isFormValid()) && this.isValidated()
           && <h2 className={ overallValidity ? 'valid' : 'invalid' }>
             { formType && isImy && whdResult && `IMY Structure: ${ overallValidity ? 'Valid' : 'Invalid' }`}
             { formType && !isImy && `${ formFamily }: ${ this.getFormValidityStatus() }` }
           </h2> }
          {!this.isExpired() && !this.isValidated() && <h2 className="none">{validationResult && formFamily}: Not Validated</h2> }
        </div>
        <div className="validation-result-content">
          { isValidatingForm && <div className="validating-progress" style={ { display: 'flex', flexDirection: 'column' } }>
            <p style={ { paddingBottom: '2em' } }>
              Please wait while a validation process is performed.
            </p>
            <progress className="progress" max="100" />
          </div> }
          { !isValidatingForm && this.isValidated() && <div className="validation-result-message">
            <IndividualValidationResult
              key={ validationResult.formId }
              formId={ validationResult.formId }
              customerId={ validationResult.customerId }
              customerName={ validationResult.customerName }
              isOnlineForm={ isOnlineForm }
              formType={ validationResult.formType }
              valid={ validationResult.valid }
              rejections={ validationResult.rejections }
              warnings={ validationResult.warnings }
              overrides={ validationResult.overrides }
              isWithTreaty={ validationResult.isWithTreaty }
              isLocked={ validationResult.isLocked }
              workflowStage={ validationResult.workflowStage }
              expiryDate={ validationResult.validUntil }
              expiryReasonDesc={ validationResult.expiryReasonDescription }
              showValidity={ isImy }
              piiValidationCodes={ piiValidationCodes }
              isPiiProtectionEnabled={ isPiiProtectionEnabled }
              tinMatchResult={ tinMatchResult }
              onCheckTin={ onCheckTin }
              showTinMatching={ showTinMatching }
              tinMatchFormProps={ tinMatchFormProps }
              isFormDirty={ isFormDirty }
            />
            {
              validationResult.childResults != null && [...validationResult.childResults].length > 0
                && [...validationResult.childResults].map((x, i) => x != null && (<IndividualValidationResult
                  key={ validationResult.childResults[i].formId }
                  formId={ validationResult.childResults[i].formId }
                  customerId={ validationResult.childResults[i].customerId }
                  customerName={ validationResult.childResults[i].customerName }
                  isOnlineForm={ isOnlineForm }
                  formType={ validationResult.childResults[i].formType }
                  valid={ validationResult.childResults[i].valid }
                  rejections={ validationResult.childResults[i].rejections }
                  warnings={ validationResult.childResults[i].warnings }
                  overrides={ validationResult.childResults[i].overrides }
                  isWithTreaty={ validationResult.childResults[i].isWithTreaty }
                  isLocked={ validationResult.childResults[i].isLocked }
                  workflowStage={ validationResult.workflowStage }
                  expiryDate={ validationResult.childResults[i].validUntil }
                  expiryReasonDesc={ validationResult.childResults[i].expiryReasonDescription }
                  piiValidationCodes={ piiValidationCodes }
                  isPiiProtectionEnabled={ isPiiProtectionEnabled }
                  tinMatchResult={ tinMatchResult }
                  onCheckTin={ onCheckTin }
                  showTinMatching={ showTinMatching }
                  tinMatchFormProps={ this.getChildTinMatchFormProps(validationResult.childResults[i].formId) }
                  isImyChildResult
                  isFormDirty={ isFormDirty }
                />))
            }
            { showWitholdingValidation && whdResult != null && <WhdStatementValidationResult result={ whdResult } /> }
            {
              isSummaryDownloadEnabled
              && AccessControl.hasPermission(permissions, [ActivityPerms.scannedDownloadValidationSummary])
              && <div className="form-group submit-group">
                <button
                  id="download-form-summary-btn"
                  className="btn"
                  type="button"
                  onClick={ this.onDownloadResultsClick }
                  disabled={ isDownloadingFormSummary }
                >Download Results</button>
              </div>
            }
            </div> }
          { !isValidatingForm && !this.isValidated() && <div className="validation-result-message">
            <p>
              This form has not yet been validated.
              Click the &quot;{ isOnlineForm ? 'Check and Submit' : 'Validate form' }&quot; button below to receive the validation results.
            </p>
          </div> }
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapDispatchToProps
)(ValidationResultsSection);
